import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { ReactComponent as PaperclipIcon } from 'assets/icons/paperclip.svg';
import { ReactComponent as AllegroLogo } from 'assets/img/allegro_logo.svg';
import formImage from 'assets/img/analysis-form-image.webp';
import { ReactComponent as DecathlonLogo } from 'assets/img/decathlon_logo.svg';
import { ReactComponent as EmpikLogo } from 'assets/img/empik_logo.svg';
import { ReactComponent as SephoraLogo } from 'assets/img/sephora_logo.svg';
import { Button, Checkbox, FakeInput, Input, Modal, PhoneInput, useValidator } from 'components/index';
import { notifyApiError, notifyCommon, notifyDanger } from 'components/layout/Toasts';

import { LandingApi } from 'src/api';

import AnalysisInvoices from '../AnalysisInvoices';

import style from './AnalysisForm.module.scss';

const AnalysisForm = () => {
  const { t } = useTranslation('page');
  const validator = useValidator();

  const [isLoading, setIsLoading] = useState(false);
  const [clinicsName, setClinicsName] = useState('');
  const [clinicsNip, setClinicsNip] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [monthlyExpenses, setMonthlyExpenses] = useState('');
  const [voucherStore, setVoucherStore] = useState(null);
  const [voucherEmail, setVoucherEmail] = useState('');
  const [files, setFiles] = useState([]);
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [regulationsAccepted, setRegulationsAccepted] = useState(false);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);

  const resetForm = () => {
    setClinicsName('');
    setClinicsNip('');
    setPhoneNumber('');
    setContactEmail('');
    setMonthlyExpenses('');
    setVoucherStore(null);
    setVoucherEmail('');
    setFiles([]);
    setPrivacyPolicyAccepted(false);
    setRegulationsAccepted(false);
    setIsAttachmentModalOpen(false);
    validator.hideMessages();
  };

  const handleSubmit = async () => {
    if (!validator.allValid()) {
      validator.showMessages();
      return;
    }
    if (!voucherStore) {
      notifyDanger([t('analysis.form.voucherStoreRequired')]);
      return;
    }

    const payload = {
      clinics_name: clinicsName,
      nip: clinicsNip,
      phone_number: phoneNumber,
      email: contactEmail,
      monthly_expenses: Number(monthlyExpenses),
      preferred_voucher: voucherStore,
      voucher_send_to: voucherEmail,
      ...(files.length > 0 && { invoices: files })
    };

    try {
      setIsLoading(true);
      await LandingApi.signUpForVoucher(payload);
      notifyCommon([t('analysis.form.messageSend')]);
      resetForm();
    } catch (error) {
      notifyApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={style.container}>
      <div className={style.imageWrapper}>
        <div className={style.text}>
          <h1 className={style.title}>
            <Trans
              i18nKey={'page:analysis.main.title'}
              components={{
                Underline: <span className={style.underline} />
              }}
            />
          </h1>
          <p className={style.description}>{t('analysis.main.description')}</p>
        </div>
        <img
          className={style.image}
          src={formImage}
          alt=''
          draggable='false'
        />
      </div>

      <div className={style.main}>
        <div
          id='form'
          className={style.form}
        >
          <h2 className={style.title}>{t('analysis.form.title')}</h2>

          <section className={style.section}>
            <h3 className={style.title}>{t('analysis.form.clinicsData')}</h3>
            <div className={style.row}>
              <Input
                id='clinicsName'
                wrapperClass={style.input}
                placeholder={t('analysis.form.clinicsName')}
                value={clinicsName}
                onChange={(e) => setClinicsName(e.target.value)}
                validator={validator}
                rule='required'
              />
              <Input
                id='clinicsNip'
                wrapperClass={style.input}
                placeholder={t('analysis.form.clinicsNip')}
                value={clinicsNip}
                onChange={(e) => setClinicsNip(e.target.value)}
                validator={validator}
                rule='required|nip'
              />
              <PhoneInput
                placeholder={t('analysis.form.phoneNumber')}
                value={phoneNumber}
                onChange={setPhoneNumber}
                wrapperStyle={style.phoneInputWrapper}
                className={style.phoneInput}
                validator={validator}
                rule={'required|phone'}
              />
              <Input
                id='contactEmail'
                wrapperClass={style.input}
                placeholder={t('analysis.form.contactEmail')}
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                validator={validator}
                rule='required|email'
              />
            </div>
          </section>

          <section className={style.section}>
            <h3 className={style.title}>{t('analysis.form.purchaseInfo')}</h3>
            <div className={style.group}>
              <Input
                type='number'
                id='monthlyExpenses'
                wrapperClass={style.input}
                placeholder={t('analysis.form.monthlyExpenses')}
                value={monthlyExpenses}
                onChange={(e) => setMonthlyExpenses(e.target.value)}
                validator={validator}
                rule='required'
              />
              <FakeInput
                className={style.fakeInput}
                onClick={() => setIsAttachmentModalOpen(true)}
              >
                <span>
                  {files.length === 0
                    ? t('analysis.form.attachInvoices')
                    : t('analysis.form.invoicesAttached', { count: files.length })}
                </span>
                <PaperclipIcon />
              </FakeInput>
              <p className={style.info}>{t('analysis.form.attachInvoicesDescription')}</p>
            </div>
          </section>

          <section className={style.section}>
            <h3 className={style.title}>{t('analysis.form.voucherPreferences')}</h3>
            <p className={style.description}>{t('analysis.form.voucherPreferencesDescription')}</p>

            <div className={style.stores}>
              <button
                onClick={() => setVoucherStore('Decathlon')}
                className={classNames(style.store, { [style.selected]: voucherStore === 'Decathlon' })}
              >
                <DecathlonLogo />
              </button>
              <button
                onClick={() => setVoucherStore('Allegro')}
                className={classNames(style.store, { [style.selected]: voucherStore === 'Allegro' })}
              >
                <AllegroLogo />
              </button>
              <button
                onClick={() => setVoucherStore('Sephora')}
                className={classNames(style.store, { [style.selected]: voucherStore === 'Sephora' })}
              >
                <SephoraLogo />
              </button>
              <button
                onClick={() => setVoucherStore('Empik')}
                className={classNames(style.store, { [style.selected]: voucherStore === 'Empik' })}
              >
                <EmpikLogo />
              </button>
            </div>

            <Input
              id='voucherEmail'
              wrapperClass={style.input}
              placeholder={t('analysis.form.voucherEmail')}
              value={voucherEmail}
              onChange={(e) => setVoucherEmail(e.target.value)}
              validator={validator}
              rule='required|email'
            />
          </section>

          <section className={style.section}>
            <Checkbox
              id='dataProcessingConsent'
              className={style.checkbox}
              checked={privacyPolicyAccepted}
              onChange={(e) => setPrivacyPolicyAccepted(e.target.checked)}
              validator={validator}
              rule='required'
              contentNode={
                <p className={style.checkboxText}>
                  <Trans
                    i18nKey={'page:analysis.form.dataProcessingConsent'}
                    components={{
                      Link: (
                        <Link
                          className={style.link}
                          to={'/privacy-policy'}
                        />
                      )
                    }}
                  />
                </p>
              }
            />
            <Checkbox
              id='regulationsConsent'
              className={style.checkbox}
              checked={regulationsAccepted}
              onChange={(e) => setRegulationsAccepted(e.target.checked)}
              validator={validator}
              rule='required'
              contentNode={
                <p className={style.checkboxText}>
                  <Trans
                    i18nKey={'page:analysis.form.regulationsConsent'}
                    components={{
                      Link: (
                        <Link
                          className={style.link}
                          to={'/analysis/regulations'}
                        />
                      )
                    }}
                  />
                </p>
              }
            />
          </section>

          <Button
            label={t('analysis.form.submit')}
            onClick={handleSubmit}
            isLoading={isLoading}
          />
        </div>
      </div>

      <Modal
        title={t('analysis.form.attachInvoices')}
        visible={isAttachmentModalOpen}
        onClose={() => setIsAttachmentModalOpen(false)}
      >
        <AnalysisInvoices
          files={files}
          setFiles={setFiles}
          onSubmit={() => setIsAttachmentModalOpen(false)}
        />
      </Modal>
    </div>
  );
};

export default AnalysisForm;
